import { supabase } from '../lib/supabase';

class ConversasService {
  // Buscar todas as conversas com estatísticas
  async getAllConversas() {
    try {
      const { data: chats, error } = await supabase
        .from('chats')
        .select(`
          id,
          aluno_id,
          materia,
          escola,
          ano_escolar,
          periodo,
          data_criacao,
          data_ultima_atividade,
          status,
          titulo,
          total_mensagens,
          alunos!inner(
            id,
            nome,
            email,
            escola_id
          )
        `)
        .order('data_ultima_atividade', { ascending: false });

      if (error) {
        console.error('Erro ao buscar conversas:', error);
        throw error;
      }

      // Processar dados para o formato esperado pelo componente
      const conversasProcessadas = chats?.map(chat => ({
        id: chat.id,
        titulo: chat.titulo || `Chat de ${chat.materia} - ${chat.alunos.nome}`,
        aluno: {
          id: chat.aluno_id,
          nome: chat.alunos.nome,
          email: chat.alunos.email,
          escola_id: chat.alunos.escola_id
        },
        materia: chat.materia,
        escola: chat.escola,
        anoEscolar: chat.ano_escolar,
        periodo: chat.periodo,
        mensagens: chat.total_mensagens || 0,
        ultimaMensagem: chat.data_ultima_atividade,
        dataCriacao: chat.data_criacao,
        status: chat.status
      })) || [];

      return conversasProcessadas;
    } catch (error) {
      console.error('Erro no service getAllConversas:', error);
      throw error;
    }
  }

  // Buscar mensagens de uma conversa específica
  async getMensagensConversa(chatId) {
    try {
      console.log('🔍 Buscando mensagens para chat ID:', chatId);
      
      const { data: mensagens, error } = await supabase
        .from('mensagens_chat')
        .select(`
          id,
          chat_id,
          tipo_mensagem,
          conteudo,
          data_envio,
          tokens_usados,
          modelo_ia,
          metadata
        `)
        .eq('chat_id', chatId)
        .order('data_envio', { ascending: true });

      if (error) {
        console.error('❌ Erro ao buscar mensagens:', error);
        throw error;
      }

      console.log('📝 Mensagens encontradas:', mensagens?.length || 0);
      console.log('📋 Tipos de mensagem:', mensagens?.map(m => m.tipo_mensagem) || []);
      
      // Debug: mostrar distribuição por tipo
      if (mensagens && mensagens.length > 0) {
        const tipos = mensagens.reduce((acc, msg) => {
          acc[msg.tipo_mensagem] = (acc[msg.tipo_mensagem] || 0) + 1;
          return acc;
        }, {});
        console.log('📊 Distribuição por tipo:', tipos);
      }

      return mensagens || [];
    } catch (error) {
      console.error('❌ Erro no service getMensagensConversa:', error);
      throw error;
    }
  }

  // Buscar estatísticas gerais das conversas
  async getEstatisticasConversas() {
    try {
      // Buscar contagem total de chats
      const { count: totalChats, error: errorChats } = await supabase
        .from('chats')
        .select('*', { count: 'exact', head: true });

      if (errorChats) {
        throw errorChats;
      }

      // Buscar contagem total de mensagens
      const { count: totalMensagens, error: errorMensagens } = await supabase
        .from('mensagens_chat')
        .select('*', { count: 'exact', head: true });

      if (errorMensagens) {
        throw errorMensagens;
      }

      // Buscar número de alunos únicos que têm chats
      const { data: alunosUnicos, error: errorAlunos } = await supabase
        .from('chats')
        .select('aluno_id')
        .not('aluno_id', 'is', null);

      if (errorAlunos) {
        throw errorAlunos;
      }

      const participantesUnicos = new Set(alunosUnicos?.map(chat => chat.aluno_id)).size;

      return {
        totalConversas: totalChats || 0,
        totalMensagens: totalMensagens || 0,
        totalParticipantes: participantesUnicos || 0
      };
    } catch (error) {
      console.error('Erro no service getEstatisticasConversas:', error);
      throw error;
    }
  }

  // Buscar conversas por filtros
  async getConversasFiltradas(filtros = {}) {
    try {
      let query = supabase
        .from('chats')
        .select(`
          id,
          aluno_id,
          materia,
          escola,
          ano_escolar,
          periodo,
          data_criacao,
          data_ultima_atividade,
          status,
          titulo,
          total_mensagens,
          alunos!inner(
            id,
            nome,
            email,
            escola_id
          )
        `);

      // Aplicar filtros
      if (filtros.materia) {
        query = query.eq('materia', filtros.materia);
      }

      if (filtros.status) {
        query = query.eq('status', filtros.status);
      }

      if (filtros.anoEscolar) {
        query = query.eq('ano_escolar', filtros.anoEscolar);
      }

      if (filtros.escola) {
        query = query.eq('escola', filtros.escola);
      }

      if (filtros.alunoNome) {
        query = query.ilike('alunos.nome', `%${filtros.alunoNome}%`);
      }

      // Ordenar por última atividade
      query = query.order('data_ultima_atividade', { ascending: false });

      const { data: chats, error } = await query;

      if (error) {
        console.error('Erro ao buscar conversas filtradas:', error);
        throw error;
      }

      // Processar dados
      const conversasProcessadas = chats?.map(chat => ({
        id: chat.id,
        titulo: chat.titulo || `Chat de ${chat.materia} - ${chat.alunos.nome}`,
        aluno: {
          id: chat.aluno_id,
          nome: chat.alunos.nome,
          email: chat.alunos.email,
          escola_id: chat.alunos.escola_id
        },
        materia: chat.materia,
        escola: chat.escola,
        anoEscolar: chat.ano_escolar,
        periodo: chat.periodo,
        mensagens: chat.total_mensagens || 0,
        ultimaMensagem: chat.data_ultima_atividade,
        dataCriacao: chat.data_criacao,
        status: chat.status
      })) || [];

      return conversasProcessadas;
    } catch (error) {
      console.error('Erro no service getConversasFiltradas:', error);
      throw error;
    }
  }

  // Buscar conversas por matéria
  async getConversasPorMateria() {
    try {
      const { data: materias, error } = await supabase
        .from('chats')
        .select('materia')
        .not('materia', 'is', null);

      if (error) {
        throw error;
      }

      // Contar conversas por matéria
      const contadorMaterias = {};
      materias?.forEach(chat => {
        contadorMaterias[chat.materia] = (contadorMaterias[chat.materia] || 0) + 1;
      });

      return contadorMaterias;
    } catch (error) {
      console.error('Erro no service getConversasPorMateria:', error);
      throw error;
    }
  }

  // Atualizar status de uma conversa
  async atualizarStatusConversa(chatId, novoStatus) {
    try {
      const { data, error } = await supabase
        .from('chats')
        .update({ status: novoStatus })
        .eq('id', chatId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar status da conversa:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no service atualizarStatusConversa:', error);
      throw error;
    }
  }

  // Formatar data para exibição
  formatarData(dataString) {
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  }

  // Calcular tempo desde a última mensagem
  calcularTempoUltimaMensagem(dataString) {
    try {
      const agora = new Date();
      const ultimaMensagem = new Date(dataString);
      const diferenca = agora - ultimaMensagem;

      const minutos = Math.floor(diferenca / (1000 * 60));
      const horas = Math.floor(diferenca / (1000 * 60 * 60));
      const dias = Math.floor(diferenca / (1000 * 60 * 60 * 24));

      if (minutos < 60) {
        return `${minutos} min atrás`;
      } else if (horas < 24) {
        return `${horas}h atrás`;
      } else {
        return `${dias} dias atrás`;
      }
    } catch (error) {
      return 'Tempo indisponível';
    }
  }
}

export default new ConversasService();
